import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { OpenAI } from "openai";
import { OPENROUTER_API_URL, OPENROUTER_API_KEY } from "../../constants";

type CampaignScopePayload = {
  executorOwnerId?: string;
  ownerId?: string;
  userId?: string;
  workspaceId?: string;
};

type CampaignMutationPayload = CampaignScopePayload & {
  campaignName: string;
  actions: any[];
  campaignStatus?: string;
  prospects: any[];
  listId?: string;
  [key: string]: any;
};

type CampaignUpdatePayload = CampaignScopePayload & {
  campaignId: string;
  campaignStatus?: string;
  campaignName?: string;
  actions?: any[];
  prospects?: any[];
  campaignStartDate?: string;
  [key: string]: any;
};

export const login = createAsyncThunk(
  "owner/login",
  async (data: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/owner/login", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const createList = createAsyncThunk(
  "owner/addList",
  async (
    data: {
      ownerId?: string;
      userId?: string;
      listName: string;
      description: string;
      workspaceId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/prospect/createProspectList", data);
      console.log(response.data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateList = createAsyncThunk(
  "owner/updateList",
  async (
    data: {
      listId: string;
      listName?: string;
      description?: string;
      ownerId?: string;
      userId?: string;
      workspaceId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/prospect/updateProspectList", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteList = createAsyncThunk(
  "owner/deleteList",
  async (
    data: {
      listId: string;
      allProspectsListId?: string;
      ownerId?: string;
      userId?: string;
      workspaceId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/prospect/deleteProspectList", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const createCampaign = createAsyncThunk(
  "owner/createCampaign",
  async (data: CampaignMutationPayload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/campaign/createCampaign", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message;
      return rejectWithValue(serverMessage);
    }
  }
);
export const saveCampaign = createAsyncThunk(
  "owner/saveCampaign",
  async (data: CampaignMutationPayload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/campaign/saveCampaign", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message;
      return rejectWithValue(serverMessage);
    }
  }
);

export const createTemplate = createAsyncThunk(
  "owner/createTemplate",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post("/template/createTemplate", data);

      console.log(response.data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.response.data.message);
    }
  }
);

export const updateTemplate = createAsyncThunk(
  "owner/updateTemplate",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post("/template/updateTemplate", data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Failed to update template"
      );
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  "owner/deleteTemplate",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post("/template/deleteTemplates", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateOwner = createAsyncThunk(
  "owner/updateOwner",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post("/owner/updateOwner", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "owner/updateUserProfile",
  async (
    data: { userId: string; email?: string; firstName?: string; lastName?: string; name?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/owner/updateUserProfile", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error?.response?.data?.msg || error.message);
    }
  }
);

export const updateDailyQuota = createAsyncThunk(
  "owner/updateDailyQuota",
  async (
    data: {
      ownerId: string;
      dailyQuota: {
        invitations: number;
        messages: number;
        visits: number;
        follows: number;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/owner/updateDailyQuota", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const launchCampaign = createAsyncThunk(
  "owner/launchCampaign",
  async (
    data: CampaignScopePayload & { campaignId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/campaign/launchCampaign", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message;
      return rejectWithValue(serverMessage);
    }
  }
);

export const updateCampaign = createAsyncThunk(
  "owner/updateCampaign",
  async (data: CampaignUpdatePayload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/campaign/editCampaign", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        error?.message;
      return rejectWithValue(serverMessage);
    }
  }
);

export const createTag = createAsyncThunk(
  "owner/createTag",
  async (
    data: {
      tagName: string;
      ownerId?: string;
      userId?: string;
      workspaceId?: string;
      /** Optional 6-digit hex with or without leading # */
      color?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/tags/createTag", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const addTagToProspect = createAsyncThunk(
  "owner/addTagToProspect",
  async (
    data: {
      prospectIds: string[];
      tagId: string;
      listId?: string;
      ownerId?: string;
      userId?: string;
      workspaceId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/tags/addTag", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const removeTagFromProspect = createAsyncThunk(
  "owner/removeTagFromProspect",
  async (
    data: {
      prospectIds: string[];
      tagId: string;
      listId?: string;
      ownerId?: string;
      userId?: string;
      workspaceId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post("/tags/removeTag", data);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const generateAIMessage = createAsyncThunk(
  'templates/generateAIMessage',
  async (inputs: {
    target: string;
    valueProposition: string;
    differentiatingFactor: string;
    callToAction: string;
  }, { rejectWithValue }) => {
    try {
      const client = new OpenAI({
        baseURL: OPENROUTER_API_URL,
        apiKey: OPENROUTER_API_KEY,
        dangerouslyAllowBrowser: true
      });

      const response = await client.chat.completions.create({
        model: "meta-llama/llama-3.3-70b-instruct:free",
        messages: [{
          role: "user",
          content: `Create a highly engaging cold LinkedIn outreach message which will be sent to 1000 prospects on LinkedIn. Consider yourself a LinkedIn lead generation expert (don't mention it) who knows how to write messages your ICP wants to hear, and you are working on behalf of a company (whose ICP is different) that has provided these four inputs.


1. INTRODUCTION (1-2 lines only)
- Hello or Hey or Hi or Greetings {{firstName}}, 2 line break.
- Open with a specific, relevant, and natural observation or compliment—such as referencing the recipient's recent post, achievement, company milestone, or a notable industry trend. If unavailable, use a thoughtful, industry-relevant insight or question. Avoid generic phrases like "I noticed [challenge] is top-of-mind for [target] like you."
- Establish credibility: "As [your role] at [your company], we've helped [similar ${inputs.target}] achieve [X% improvement] through ${inputs.differentiatingFactor},"
- Add a line showing you've done your homework, e.g., "I came across your recent work in [industry/trend] and was impressed by your perspective. Not necessary same as this, it just for an example.

2. VALUE PROPOSITION (2-3 lines only)
- Clearly explain how you help ${inputs.target} solve their key challenges with ${inputs.valueProposition}, focusing on benefits and outcomes.
- Use a specific, relatable example or metric: "Our approach to ${inputs.valueProposition} helps [${inputs.target}] achieve [specific outcome]—recent users saw [metric] in [timeframe].


3. DIFFERENTIATION (1-2 lines only)
- Create urgency and relevance: "With [industry trend from ${inputs.valueProposition}], this solution is now essential for [specific scenario].
- Reinforce your unique value: "What sets us apart is ${inputs.differentiatingFactor}
- Highlight what makes you different: "Unlike [common alternative], ${inputs.differentiatingFactor} delivers [concrete benefit] for [specific aspect of operation]."

4. CALL TO ACTION (1 line only)
- Make your CTA clear and low-pressure: "${inputs.callToAction} — just a friendly chat to see if there's a fit".
- Add exclusivity or scarcity: "We're reserving this for a select group of companies/partners, should I save your spot?
- Optionally, pose a friendly question to encourage a reply: "Would you be open to a quick 15-minute call this week to see if this could help you too?
- End with a thank you, best regards etc

TONE & FORMATTING REQUIREMENTS:

- Output only the message body, with no introduction, heading, or explanation. Do not include phrases like "Here is your message" or "Here is a highly engaging cold LinkedIn outreach message.
- Warm, professional, and confident language (never salesy or pushy)
- Check and rewrite the message for natural, human flow—avoid robotic or AI-generated language
- Humanize the message, it should not look like an AI generated message with some gpt or other AI
- Focused on the recipient's needs and goals, not just your service/product
- Use power verbs like: "transform," "breakthrough," "proven," etc, when describing ${inputs.differentiatingFactor}
- Use emotional hooks like: "Imagine..." or "What if..." tied to ${inputs.valueProposition}
- Ensure Perfect grammar and formatting
- 4 clear paragraphs(1-2 lines each) with line breaks, keep it short, concise and crispy
- The output must be ready to copy and send directly as a LinkedIn message`
        }],
        temperature: 0.7,
        max_tokens: 200
      });
      
      return response.choices[0].message.content;
    } catch (error: any) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);
